import {
  hasResolvedDocumentValue,
  useDocument,
} from "@shared/components/providers/SupergrainProvider"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { reportFollowedLoadError } from "./followed-course-errors"

/**
 * A followed course lives in its own planbook, so its days are read off that planbook's rotation
 * calendar rather than the one the wrapping lesson was rendered with. Both come back null until
 * the documents land, and stay null if either fails to load.
 */
export function useFollowedCoursePlanbook(followedCourse: Course | null): {
  planbook: Planbook | null
  rotationCalendar: RotationCalendar | null
  hasError: boolean
} {
  const planbookHandle = useDocument(
    "planbook",
    followedCourse?.relationships.planbook?.data.id ?? null,
  )
  const planbook = hasResolvedDocumentValue(planbookHandle) ? planbookHandle.value : null

  const rotationCalendarHandle = useDocument(
    "rotation-calendar",
    planbook?.relationships.rotationCalendar.data.id ?? null,
  )
  const rotationCalendar = hasResolvedDocumentValue(rotationCalendarHandle)
    ? rotationCalendarHandle.value
    : null

  const error =
    planbookHandle.status === "error"
      ? planbookHandle.error
      : rotationCalendarHandle.status === "error"
        ? rotationCalendarHandle.error
        : null
  if (error !== null) reportFollowedLoadError(error)

  return { planbook, rotationCalendar, hasError: error !== null }
}
